import { defineStore } from 'pinia';
import API from '@/shared/helpers/API.js';
import {useEpsonStore, useUserStore} from "@/console-new/stores";
import {getCompatibleDate, getLocalDateFromServer} from "@/shared/helpers/dates";
import {getGroupIdentifier, getOrderStatus, normalizeOrders} from "@/console-new/helpers/orders";
import EscPosOrders from "@/console-sun/helpers/EscPosOrders";
import {getNative, isAndroid, isNative} from "@/shared/helpers/webapp";

const ORDERS_BROADCAST_CHANNEL_NAME = 'qromo-orders-display';
let _ordersBroadcastChannel = null;

export const useOrdersStore = defineStore('orders', {
	state: () => ({
		orderGroups: [],
		selectedGroupId: null,
		filterRooms: true,
		filterCounter: true,
		filterTakeaway: true,
		filterDelivery: true,
		fiscalFilter: 'all',
		count: 0,
		countGroups: 0,
		showCompleted: false,
		showPrepared: false,
		search: null,
		searchLoading: false,
		onlyMyOrders: getOnlyMyOrdersValue(),
		page: 0,
		pageSize: 25,
		loaded: false,
		lastTimeSeen: null,
		audioNewOrder: null,
		showMobileOrderSummary: false,
		transitioningMobileOrderSummary: false,
		isUsingOldQrs: false,
		deliveryOrderInfo: null,
		sendReceiptData: null,
		sendReceiptEpsonData: null,
		ctaIsDueForOrderId: null,
		ctaIsPaymentPartial: null,
		STATUS: {
			unconfirmed: "unconfirmed",
			waiting: "waiting",
			confirmed: "confirmed",
			accepted: "accepted",
			preparing: "preparing",
			prepared: "prepared",
			completed: "completed",
			canceled: "canceled",
			failed: "failed"
		},
		tablesToCheck: false,
		isKitchenMonitor: false,
		selectedKitchenItemId: null,
		selectedKitchenOrderMode: null,
	}),
	getters: {
		ordersUngrouped: state => {
			let orders = [];
			state.orderGroups.forEach(group => {
				orders = orders.concat(group.orders);
			});
			return orders;
		},
		selectedOrderGroup: state => {
			if(state.selectedGroupId) {
				return state.orderGroups.find(group => group.groupIdentifier === state.selectedGroupId);
			} else {
				return null;
			}
		},
		totalOrderCards: state => {
			if(state.showCompleted || state.search) {
				return state.count;
			} else {
				return state.countGroups;
			}
		},
	},
	actions: {
		resetData() {
			this.orderGroups = [];
			this.selectedGroupId = null;
			this.count = 0;
			this.countGroups = 0;
			this.loaded = false;
			this.lastTimeSeen = null;
			this.isUsingOldQrs = false;
		},
		activateMobileOrderSummary() {
			this.transitioningMobileOrderSummary = true;
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					this.showMobileOrderSummary = true;
				});
			});
		},
		deactivateMobileOrderSummary() {
			this.showMobileOrderSummary = false;
			setTimeout(() => {
				this.transitioningMobileOrderSummary = false;
			}, 500);
		},
		resetOrders() {
			this.showCompleted = false;
			this.search = null;
			this.page = 0;
			setTimeout(() => {
				this.refreshOrders();
			}, 100);
		},
		playNewOrderSound() {
			if(!isAndroid()) {
				const userStore = useUserStore();
				if(!userStore.currBusiness.notification_sound) {
					return;
				}
				if(this.audioNewOrder) {
					this.audioNewOrder.play().catch(() => {
						console.warn("Audio play not allowed");
					});
				} else {
					this.audioNewOrder = (new Audio('https://img.qromo.io/audio/'+userStore.currBusiness.notification_sound+'.mp3'));
					this.audioNewOrder.play().catch(() => {
						console.warn("Audio play not allowed");
					});
				}
			}
		},
		refreshUncompletedOrders(callback = null) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) {
				return;
			}
			let requestIdentifier = userStore.currBusinessId+"_"+this.filterRooms+"_"+this.filterCounter+"_"+this.filterDelivery+"_"+this.filterTakeaway+"_"+this.pageSize+"_"+this.page+"_"+this.showCompleted+"_"+this.search+"_"+this.onlyMyOrders+"_"+this.fiscalFilter;
			API.init().getOrdersNew(userStore.currBusinessId, this.filterRooms ? 1 : 0, this.filterCounter ? 1 : 0, this.filterDelivery ? 1 : 0, this.filterTakeaway ? 1 : 0, this.onlyMyOrders ? 1 : 0, this.pageSize, this.page, this.fiscalFilter, (response) => {

				if(response && response.result === "OK" && response.orders) {
					if(this.lastTimeSeen === null || getCompatibleDate(response.time) > getCompatibleDate(this.lastTimeSeen)) {
						this.lastTimeSeen = response.time;
					}

					let requestIdentifierAfter = userStore.currBusinessId+"_"+this.filterRooms+"_"+this.filterCounter+"_"+this.filterDelivery+"_"+this.filterTakeaway+"_"+this.pageSize+"_"+this.page+"_"+this.showCompleted+"_"+this.search+"_"+this.onlyMyOrders+"_"+this.fiscalFilter;
					if(requestIdentifier !== requestIdentifierAfter) {
						callback && callback(false);
						return;
					}

					if(response.orders.length > 0) {

						// If the selected group is not found and order is 'qr' mode, it could be that the payment method changed from cash to multi
						let testIdentifier = null;
						if(this.selectedOrderGroup && this.selectedOrderGroup.orders.length > 0 && this.selectedOrderGroup.orders[0].order_mode === "qr" && this.selectedOrderGroup.orders[0].type === "cash") {
							testIdentifier = getGroupIdentifier(this.selectedOrderGroup.orders[0], true, null, "multi");
						}

						this.orderGroups = normalizeOrders(response.orders, response.items, response.items_products, response.addresses, response.rates, response.payments, response.plugins, true, false, false, response?.rooms);
						if(this.orderGroups.length > 0) {
							if(this.selectedGroupId) {
								let group = this.orderGroups.find(group => group.groupIdentifier === this.selectedGroupId);
								if(!group) {
									if(testIdentifier) {
										group = this.orderGroups.find(group => group.groupIdentifier === testIdentifier);
										if(group) {
											this.selectedGroupId = group.groupIdentifier;
										} else {
											this.selectedGroupId = this.orderGroups[0].groupIdentifier;
										}
									} else {
										this.selectedGroupId = this.orderGroups[0].groupIdentifier;
									}
								}
							} else {
								this.selectedGroupId = this.orderGroups[0].groupIdentifier;
							}
						} else {
							this.selectedGroupId = null;
							if(this.showMobileOrderSummary) {
								this.deactivateMobileOrderSummary();
							}
						}
					} else {
						this.orderGroups = [];
						this.selectedGroupId = null;
						this.isUsingOldQrs = false;
						if(this.showMobileOrderSummary) {
							this.deactivateMobileOrderSummary();
						}
					}
					this.count = Number(response.count);
					this.countGroups = Number(response.count_groups);
					if(isNative()) {
						EscPosOrders.init().handleOrdersPrint(this.ordersUngrouped);
					}
					callback && callback(response.orders);
				} else {
					callback && callback(false);
				}
			});
		},
		refreshCompletedOrders(callback = null) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) {
				return;
			}
			let requestIdentifier = userStore.currBusinessId+"_"+this.filterRooms+"_"+this.filterCounter+"_"+this.filterDelivery+"_"+this.filterTakeaway+"_"+this.pageSize+"_"+this.page+"_"+this.showCompleted+"_"+this.search+"_"+this.onlyMyOrders+"_"+this.fiscalFilter;
			API.init().getOrdersCompletedNew(userStore.currBusinessId, this.filterRooms ? 1 : 0, this.filterCounter ? 1 : 0, this.filterDelivery ? 1 : 0, this.filterTakeaway ? 1 : 0, this.onlyMyOrders ? 1 : 0, this.pageSize, this.page, userStore.manualCode, this.fiscalFilter, (response) => {
				let requestIdentifierAfter = userStore.currBusinessId+"_"+this.filterRooms+"_"+this.filterCounter+"_"+this.filterDelivery+"_"+this.filterTakeaway+"_"+this.pageSize+"_"+this.page+"_"+this.showCompleted+"_"+this.search+"_"+this.onlyMyOrders+"_"+this.fiscalFilter;
				if(requestIdentifier !== requestIdentifierAfter) {
					callback && callback(false);
					return;
				}
				if(response && response.result === "OK" && response.orders) {

					if(response.orders.length > 0) {
						this.orderGroups = normalizeOrders(response.orders, response.items, response.items_products, response.addresses, response.rates, response.payments, response.plugins, false, false, true, response?.rooms);
						if(this.orderGroups.length > 0) {
							this.selectedGroupId = this.orderGroups[0].groupIdentifier;
						} else {
							this.selectedGroupId = null;
							if(this.showMobileOrderSummary) {
								this.deactivateMobileOrderSummary();
							}
						}
					} else {
						this.orderGroups = [];
						this.selectedGroupId = null;
						this.isUsingOldQrs = false;
						if(this.showMobileOrderSummary) {
							this.deactivateMobileOrderSummary();
						}
					}
					this.count = Number(response.count);

					callback && callback(response.orders);

				} else {
					callback && callback(false);
				}
			});
		},
		refreshSearchOrders(callback = null) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) {
				return;
			}
			let requestIdentifier = userStore.currBusinessId+"_"+this.filterRooms+"_"+this.filterCounter+"_"+this.filterDelivery+"_"+this.filterTakeaway+"_"+this.pageSize+"_"+this.page+"_"+this.showCompleted+"_"+this.search+"_"+this.onlyMyOrders+"_"+this.fiscalFilter;
			API.init().searchOrdersNew(userStore.currBusinessId, this.search, this.showCompleted ? 1 : 0, this.filterRooms ? 1 : 0, this.filterCounter ? 1 : 0, this.filterDelivery ? 1 : 0, this.filterTakeaway ? 1 : 0, this.onlyMyOrders ? 1 : 0, this.pageSize, this.page, userStore.manualCode, this.fiscalFilter, (response) => {
				let requestIdentifierAfter = userStore.currBusinessId+"_"+this.filterRooms+"_"+this.filterCounter+"_"+this.filterDelivery+"_"+this.filterTakeaway+"_"+this.pageSize+"_"+this.page+"_"+this.showCompleted+"_"+this.search+"_"+this.onlyMyOrders+"_"+this.fiscalFilter;
				if(requestIdentifier !== requestIdentifierAfter) {
					callback && callback(false);
					return;
				}
				if(response && response.result === "OK" && response.orders) {

					if(response.orders.length > 0) {
						let ordersSorted = response.orders.map(o => Object.assign({priority: o.priority}, o.order));
						this.orderGroups = normalizeOrders(ordersSorted, response.items, response.items_products, response.addresses, response.rates, response.payments, response.plugins, true, true, true, response?.rooms);

						if(this.orderGroups.length > 0) {
							this.selectedGroupId = this.orderGroups[0].groupIdentifier;
						} else {
							this.selectedGroupId = null;
							if(this.showMobileOrderSummary) {
								this.deactivateMobileOrderSummary();
							}
						}
					} else {
						this.orderGroups = [];
						this.selectedGroupId = null;
						this.isUsingOldQrs = false;
						if(this.showMobileOrderSummary) {
							this.deactivateMobileOrderSummary();
						}
					}
					this.count = Number(response.count);
					callback && callback(response.orders);

				} else {
					callback && callback(false);
				}
			});
		},
		refreshOrders(callback = null) {
			if(this.search) {
				this.refreshSearchOrders(callback);
			} else {
				if(this.showCompleted) {
					this.refreshCompletedOrders(callback);
				} else {
					this.refreshUncompletedOrders(callback);
				}
			}
		},
		resetOrdersFilters() {
			this.filterRooms = true;
			this.filterCounter = true;
			this.filterDelivery = true;
			this.filterTakeaway = true;
			this.pageSize = 25;
			this.page = 0;
			this.showCompleted = false;
			this.showPrepared = false;
			this.search = null;
			this.lastTimeSeen = null;
		},
		getNewOrderData(orderId, callback) {

			let callbackCalled = false;
			try {

				let userStore = useUserStore();
				if(!userStore.currBusinessId) {
					callback && !callbackCalled && callback(false);
					callbackCalled = true;
					return;
				}
				let requestIdentifier = userStore.currBusinessId+"_"+this.filterRooms+"_"+this.filterDelivery+"_"+this.filterTakeaway+"_"+this.pageSize+"_"+this.page+"_"+this.showCompleted+"_"+this.search+"_"+this.onlyMyOrders;

				API.init().getOrderWithId(userStore.currBusinessId, orderId,  (response) => {

					let requestIdentifierAfter = userStore.currBusinessId+"_"+this.filterRooms+"_"+this.filterDelivery+"_"+this.filterTakeaway+"_"+this.pageSize+"_"+this.page+"_"+this.showCompleted+"_"+this.search+"_"+this.onlyMyOrders;
					if(requestIdentifier !== requestIdentifierAfter) {
						callback && !callbackCalled && callback(false);
						callbackCalled = true;
						return;
					}

					if (response && response.result === "OK") {

						callback && !callbackCalled && callback(response.order);
						callbackCalled = true;

						if(this.onlyMyOrders && response.order && response.order.staff_user_id !== userStore.user.user_id) {
							console.log("Order is not for current user, skipping");
						} else {

							response.new_orders = response.order ? [response.order] : [];
							this.handleNewOrders(response);

						}

					} else {
						callback && !callbackCalled && callback(false);
						callbackCalled = true;
					}
				}, function () {
					callback && !callbackCalled && callback(false);
					callbackCalled = true;
				});

			} catch (e) {
				callback && !callbackCalled && callback(false);
				callbackCalled = true;
			}

		},
		handleNewOrders(response) {
			if(response.new_orders && response.new_orders.length > 0) {
				let validNewOrders = this.applyNewOrders(response);
				if(validNewOrders.foundNewOrders) {

					const currUserId = useUserStore()?.user?.user_id; // Fetched once
					const shouldPlaySound = !this.isKitchenMonitor && validNewOrders.newOrders.some(newOrder =>
						newOrder.staff_user_id === null || (currUserId && newOrder.staff_user_id === currUserId)
					);
					if (shouldPlaySound) {
						this.playNewOrderSound();
					}

					if(isNative() && getNative().print) {
						EscPosOrders.init().handleOrdersPrint(validNewOrders.newOrders);
					}
				}
				if(validNewOrders.tablesToCheck) {
					this.tablesToCheck = true;
				}
			}
		},
		getFiscalUpdates() {

			const epsonStore = useEpsonStore();

			if(epsonStore.hasFiscalPrinter) {

				const userStore = useUserStore();

				API.init().getFiscalUpdates(userStore.currBusinessId, (response) => {

					if(response && response.result === "OK") {

						this.handleFiscalOrders(response);

					}

				});

			}

		},
		handleFiscalOrders(response) {
			if(response.fiscal_queue && response.fiscal_queue.orders && response.fiscal_queue.orders.length > 0) {
				console.log("Received fiscal queue: "+response.fiscal_queue.orders.length);

				let fiscalOrdersGroup = normalizeOrders(response.fiscal_queue.orders, response.fiscal_queue.items, response.fiscal_queue.items_products, response.fiscal_queue.addresses, response.fiscal_queue.rates, response.fiscal_queue.payments, response.fiscal_queue.plugins, false, !!this.search, true, response?.fiscal_queue?.rooms);
				let orders = [];
				Object.values(fiscalOrdersGroup).forEach(group => {
					orders = orders.concat(group.orders);
				});

				const epsonStore = useEpsonStore();
				epsonStore.printNewOrders(orders);

			}
		},
		isOrderInList(orderId) {
			return this.orderGroups.some(group =>
				group.orders.some(order => order.order_id === orderId)
			);
		},
		wouldNewOrdersBeVisible() {
			let wouldNewOrdersBeVisible = false;
			let isCurrentPageTheLastPage = this.page === Math.floor(this.totalOrderCards / this.pageSize);
			if(isCurrentPageTheLastPage) {
				let hasCurrentPageTheMaxNumberOfCards = this.orderGroups.length >= this.pageSize;
				if(!hasCurrentPageTheMaxNumberOfCards) {
					wouldNewOrdersBeVisible = true;
				}
			}
			return wouldNewOrdersBeVisible;
		},
		applyNewOrders(response) {

			let newOrdersGroup = [];
			let tablesToCheck = false;
			response.new_orders.sort((a,b)=> {
				return b.order_id - a.order_id;
			});
			for(let i = response.new_orders.length - 1; i >= 0; i--) {
				let order = response.new_orders[i];
				if(this.isOrderInList(order.order_id)) {
					response.new_orders.splice(i, 1);
				}
				if(order.room_table_id) {
					tablesToCheck = true;
				}
			}

			newOrdersGroup = normalizeOrders(response.new_orders, response.items, response.items_products, response.addresses, response.rates, response.payments, response.plugins, !this.showCompleted, !!this.search, !!this.showCompleted, response?.rooms);

			if(!this.showCompleted && !this.search) {

				for(let i = response.new_orders.length - 1; i >= 0; i--) {
					let order = response.new_orders[i];
					if(order.status === this.STATUS.completed) {
						continue;
					}
					this.count++;
				}

				let wouldNewOrdersBeVisible = this.wouldNewOrdersBeVisible();

				for(let i = 0; i < newOrdersGroup.length; i++) {
					let newGroup = newOrdersGroup[i];
					if(newGroup.status === this.STATUS.completed) {
						continue;
					}
					let existingGroup = this.orderGroups.find(g => g.groupIdentifier === newGroup.groupIdentifier);
					if(existingGroup) {
						existingGroup.orders = existingGroup.orders.concat(newGroup.orders);
						existingGroup.orders.sort((a, b) => {
							return b.order_id - a.order_id;
						});
						if(newGroup.status === 'preparing' && existingGroup.status === 'prepared') {
							existingGroup.status = 'preparing';
						}
						if(newGroup.type === 'multi' && existingGroup.type !== 'multi') {
							existingGroup.type = 'multi';
							existingGroup.paymentMethodName = newGroup.paymentMethodName;
							existingGroup.paymentColor = newGroup.paymentColor;
						}
						if(this.selectedGroupId !== existingGroup.groupIdentifier) {
							existingGroup.alert = true;
						} else {
							let newSeenIds = newGroup.orders.map(order => order.order_id);
							this.addToOrdersSeen(newSeenIds);
						}
					} else {
						if(wouldNewOrdersBeVisible) {
							let index = null;
							for(let j = 0; j < this.orderGroups.length; j++) {
								if(this.orderGroups[j].date_ordering > newGroup.date_ordering) {
									index = j;
									break;
								}
							}
							if(index !== null) {
								this.orderGroups.splice(index, 0, newGroup);
							} else {
								this.orderGroups.push(newGroup);
							}
						}
						this.countGroups++;
					}
				}

				if(wouldNewOrdersBeVisible) {

					if(this.selectedGroupId === null && newOrdersGroup.length > 0) {
						this.selectedGroupId = newOrdersGroup[0].groupIdentifier;
					}

				}

			}

			let orders = [];
			newOrdersGroup.forEach(group => {
				orders = orders.concat(group.orders);
			});
			return {
				foundNewOrders: response.new_orders.length > 0,
				tablesToCheck: tablesToCheck,
				newOrders: orders
			};
		},
		addToOrdersSeen(orderIds) {
			let ordersSeen = localStorage.getItem('ordersSeen');
			ordersSeen = ordersSeen ? JSON.parse(ordersSeen) : {};
			let changed = false;
			orderIds.forEach((orderId) => {
				if(!ordersSeen.hasOwnProperty(orderId)) {
					ordersSeen[orderId] = true;
					changed = true;
				}
			});
			if(changed) {
				let keys = Object.keys(ordersSeen);
				if(keys.length > 1100) {
					ordersSeen = keys.slice(-1000).reduce((obj, key) => {
						obj[key] = true;
						return obj;
					}, {});
				}
				localStorage.setItem('ordersSeen', JSON.stringify(ordersSeen));
			}
		},
		applyOrdersUpdates(updates, normalize = true, paymentsUpdates = null, callback = null) {

			let ordersUpdatesObj = {};
			updates.forEach(update=> {
				if(normalize) {
					if(update.hasOwnProperty("order_id")) {
						update.order_id = Number(update.order_id);
					} else {
						return;
					}
					if(update.hasOwnProperty("sms_sent")) {
						update.sms_sent = Number(update.sms_sent) === 1;
					}
					if(update.hasOwnProperty("fiscal_receipt")) {
						update.fiscal_receipt = Number(update.fiscal_receipt) === 1;
					}
					if(update.hasOwnProperty("fiscal_request")) {
						update.fiscal_request = Number(update.fiscal_request) === 1;
					}
					if(update.hasOwnProperty("prebill_requested")) {
						update.prebill_requested = Number(update.prebill_requested) === 1;
					}
					if(update.hasOwnProperty("paid")) {
						update.paid = Number(update.paid) === 1;
					}
					if(update.hasOwnProperty("receipt_id")) {
						update.receipt_id = update.receipt_id ? Number(update.receipt_id) : null;
					}
					if(update.hasOwnProperty("epson_receipt_id")) {
						update.epson_receipt_id = update.epson_receipt_id ? Number(update.epson_receipt_id) : null;
					}
					if(update.hasOwnProperty("invoice_id")) {
						update.invoice_id = update.invoice_id ? Number(update.invoice_id) : null;
					}
					if(paymentsUpdates && paymentsUpdates.length > 0) {
						update.payments = [];
					}
					if(update.hasOwnProperty("course")) {
						update.course = Number(update.course);
					}
					if(update.hasOwnProperty("date_last_edit")) {
						update.date_last_edit = update.date_last_edit ? getLocalDateFromServer(update.date_last_edit) : null;
					}
					if(update.hasOwnProperty("subtotal_delta")) {
						update.subtotal_delta = Number(update.subtotal_delta);
					}
					if(update.hasOwnProperty("new_order_payment")) {
						update.subtotal_delta = Number(update.new_order_payment);
					}
				}

				ordersUpdatesObj[update.order_id] = update;
			});

			if(paymentsUpdates && paymentsUpdates.length > 0) {
				paymentsUpdates.forEach(payment => {
					/*if(payment.hasOwnProperty("fiscal_request")) {
						payment.fiscal_request = payment.fiscal_request === 1;
					}
					if(payment.hasOwnProperty("fiscal_receipt")) {
						payment.fiscal_receipt = payment.fiscal_receipt === 1;
					}*/
					if(payment.hasOwnProperty("receipt_id")) {
						payment.receipt_id = payment.receipt_id ? Number(payment.receipt_id) : null;
					}
					if(payment.hasOwnProperty("epson_receipt_id")) {
						payment.epson_receipt_id = payment.epson_receipt_id ? Number(payment.epson_receipt_id) : null;
					}
					if(payment.hasOwnProperty("invoice_id")) {
						payment.invoice_id = payment.invoice_id ? Number(payment.invoice_id) : null;
					}
					if(payment.hasOwnProperty("order_id") && !ordersUpdatesObj.hasOwnProperty(payment.order_id)) {
						ordersUpdatesObj[payment.order_id] = {
							order_id: payment.order_id,
							payments: []
						};
					}
					ordersUpdatesObj[payment.order_id].payments.push(payment);
				});
			}

			let forceRefresh = false;
			groupLoop: for(let groupindex = this.orderGroups.length-1; groupindex >= 0; groupindex--) {

				for(let orderIndex = 0; orderIndex < this.orderGroups[groupindex].orders.length; orderIndex++) {

					let order = this.orderGroups[groupindex].orders[orderIndex];
					if(ordersUpdatesObj.hasOwnProperty(order.order_id)) {

						let update = ordersUpdatesObj[order.order_id];
						if(update.hasOwnProperty("date_last_edit") && ((order.date_last_edit === null && update.date_last_edit !== null) || (order.date_last_edit !== null && update.date_last_edit !== null && update.date_last_edit.getTime() !== order.date_last_edit.getTime()))) {
							forceRefresh = true;
							if(order.room_table_id) {
								this.tablesToCheck = true;
							}
							break groupLoop;
						}
						if(update.hasOwnProperty("status") && update.status !== order.status) {
							if(this.selectedGroupId === order.groupIdentifier) {
								if(update.status !== "completed") {
									this.selectedGroupId = getGroupIdentifier(order, !this.showCompleted, update.status);
								} else {
									if(groupindex+1 < this.orderGroups.length) {
										this.selectedGroupId = this.orderGroups[groupindex+1].groupIdentifier;
									} else {
										this.selectedGroupId = null;
									}
									if(this.showMobileOrderSummary) {
										this.deactivateMobileOrderSummary();
									}
								}
							}
							if(order.room_table_id) {
								this.tablesToCheck = true;
							}
							forceRefresh = true;
							break groupLoop;
						}
						if(update.hasOwnProperty("prebill_requested") && update.prebill_requested !== order.prebill_requested) {
							this.orderGroups[groupindex].orders[orderIndex].prebill_requested = update.prebill_requested;
							if(order.room_table_id) {
								this.tablesToCheck = true;
							}
						}
						if(update.hasOwnProperty("course") && update.course !== order.course) {
							let updatingOrder = this.orderGroups[groupindex].orders[orderIndex];
							updatingOrder.course = update.course;
							if(updatingOrder?.items?.length) {
								for(let item of updatingOrder.items) {
									if(item.course < update.course) {
										item.prepared = true;
									}
								}
							}
						}
						if(update.hasOwnProperty("fiscal_receipt") && update.fiscal_receipt !== order.fiscal_receipt) {
							this.orderGroups[groupindex].orders[orderIndex].fiscal_receipt = update.fiscal_receipt;
						}
						if(update.hasOwnProperty("fiscal_request") && update.fiscal_request !== order.fiscal_request) {
							this.orderGroups[groupindex].orders[orderIndex].fiscal_request = update.fiscal_request;
						}
						if(update.hasOwnProperty("receipt_id") && update.receipt_id !== order.receipt_id) {
							this.orderGroups[groupindex].orders[orderIndex].receipt_id = update.receipt_id;
						}
						if(update.hasOwnProperty("epson_receipt_id") && update.epson_receipt_id !== order.epson_receipt_id) {
							this.orderGroups[groupindex].orders[orderIndex].epson_receipt_id = update.epson_receipt_id;
						}
						if(update.hasOwnProperty("invoice_id") && update.invoice_id !== order.invoice_id) {
							this.orderGroups[groupindex].orders[orderIndex].invoice_id = update.invoice_id;
						}
						if(update.hasOwnProperty("paid") && update.paid !== order.paid) {
							this.orderGroups[groupindex].orders[orderIndex].paid = update.paid;
							if(order.groupLeader) {
								this.orderGroups[groupindex].paid = update.paid;
							}
						}
						if(update.hasOwnProperty("sms_sent") && update.sms_sent !== order.sms_sent) {
							this.orderGroups[groupindex].orders[orderIndex].sms_sent = update.sms_sent;
							if(order.groupLeader) {
								this.orderGroups[groupindex].sms_sent = update.sms_sent;
							}
						}
						if(update.hasOwnProperty("subtotal_delta") && update.subtotal_delta !== order.subtotal_delta) {
							forceRefresh = true;
							if(order.room_table_id) {
								this.tablesToCheck = true;
							}
							break groupLoop;
						}
						if(update.hasOwnProperty("type") && update.type !== order.type) {
							forceRefresh = true;
							break groupLoop;
						}
						if(update.hasOwnProperty("new_order_payment")) {
							forceRefresh = true;
							break groupLoop;
						}
						if(update.hasOwnProperty("items_prepared") && Array.isArray(update.items_prepared)) {
							// Mark specific items as prepared
							for(let item of order.items) {
								if(update.items_prepared.includes(item.order_item_id)) {
									item.prepared = true;
								}
							}
						}
						if(update.hasOwnProperty("payments")) {
							let updatePaymentsMap = {};
							update.payments.forEach(payment => {
								updatePaymentsMap[payment.order_payment_id] = payment;
							});
							for(let i = 0; i < order.payments.length; i++) {
								let payment = order.payments[i];
								if(updatePaymentsMap.hasOwnProperty(payment.order_payment_id)) {
									let updatePayment = updatePaymentsMap[payment.order_payment_id];
									if(updatePayment.hasOwnProperty("fiscal_request") && updatePayment.fiscal_request !== payment.fiscal_request) {
										this.orderGroups[groupindex].orders[orderIndex].payments[i].fiscal_request = updatePayment.fiscal_request;
									}
									if(updatePayment.hasOwnProperty("fiscal_receipt") && updatePayment.fiscal_receipt !== payment.fiscal_receipt) {
										this.orderGroups[groupindex].orders[orderIndex].payments[i].fiscal_receipt = updatePayment.fiscal_receipt;
									}
									if(updatePayment.hasOwnProperty("receipt_id") && updatePayment.receipt_id !== payment.receipt_id) {
										this.orderGroups[groupindex].orders[orderIndex].payments[i].receipt_id = updatePayment.receipt_id;
									}
									if(updatePayment.hasOwnProperty("epson_receipt_id") && updatePayment.epson_receipt_id !== payment.epson_receipt_id) {
										this.orderGroups[groupindex].orders[orderIndex].payments[i].epson_receipt_id = updatePayment.epson_receipt_id;
									}
									if(updatePayment.hasOwnProperty("invoice_id") && updatePayment.invoice_id !== payment.invoice_id) {
										this.orderGroups[groupindex].orders[orderIndex].payments[i].invoice_id = updatePayment.invoice_id;
									}
								}
							}
						}
					}
				}

			}

			if(forceRefresh) {
				this.refreshOrders(() => {
					callback && callback(true);
				});
			} else {
				callback && callback(false);
			}

		},
		initOrdersBroadcast() {
			if (_ordersBroadcastChannel) return;
			_ordersBroadcastChannel = new BroadcastChannel(ORDERS_BROADCAST_CHANNEL_NAME);
		},
		forceOrdersBroadcast() {
			if (!_ordersBroadcastChannel) return;
			let orderGroup = this.selectedOrderGroup;
			try {
				_ordersBroadcastChannel.postMessage(JSON.parse(JSON.stringify({
					selectedOrderGroup: orderGroup || null
				})));
			} catch (e) {
				// Silently ignore serialization errors
			}
		},
	},
});

function getOnlyMyOrdersValue() {
	let onlyMyOrders = localStorage.getItem('onlyMyOrders');
	return onlyMyOrders === "true";
}
